import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { devAdminAPI } from '../../../services/adminService';
import { postsAPI, socialAPI } from '../../../services/api';
import ModerationActionModal from '../../../components/modals/ModerationActionModal';
import './UserManager.css';

const UserManager = () => {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [userStats, setUserStats] = useState({ posts: 0, buddies: 0 });
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const [deleteError, setDeleteError] = useState(null);

  const [actionModal, setActionModal] = useState({ isOpen: false });
  const [isActionProcessing, setIsActionProcessing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [countryFilter, setCountryFilter] = useState('');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  // Load users
  const loadUsers = useCallback(async (page = 1) => {
    setIsLoadingUsers(true);
    setApiError(null);
    try {
      const params = {
        page,
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(countryFilter ? { country: countryFilter } : {}),
      };
      const response = await devAdminAPI.getEndUsers(params);
      const data = response?.data;
      const nestedData = data?.data || data;
      let userList = [];
      if (nestedData?.users && Array.isArray(nestedData.users)) {
        userList = nestedData.users;
      } else if (Array.isArray(nestedData)) {
        userList = nestedData;
      }
      setUsers(userList || []);
      const paginationData = nestedData?.pagination || { current_page: 1, last_page: 1, total: 0 };
      setPagination(paginationData);
      setAvailableCountries(nestedData?.available_countries || []);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load users:', error);
      setApiError(error?.response?.data?.message || error.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [searchQuery, countryFilter]);

  useEffect(() => {
    setCurrentPage(1);
    loadUsers(1);
  }, [loadUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadUsers(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, loadUsers]);

  const getUserName = (user) => {
    if (!user) return 'Unknown User';
    const firstName = typeof user.first_name === 'string' ? user.first_name : '';
    const lastName = typeof user.last_name === 'string' ? user.last_name : '';
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (user.display_name && typeof user.display_name === 'string') return user.display_name;
    if (user.username && typeof user.username === 'string') return user.username;
    if (user.email && typeof user.email === 'string') return user.email.split('@')[0];
    return 'Unknown Name';
  };

  const getUserType = (user) => {
    if (!user) return '';
    const type = user.user_type || user.userType || user.type || user.role;
    if (typeof type === 'object' && type !== null) {
      const name = type.name || type.slug || type.displayName || type.label || type.value;
      if (typeof name === 'string') {
        const labels = {
          PET_GUARDIAN: t('adminDashboard.userTypes.petGuardian', 'Pet Guardian'),
          VETERINARIAN: t('adminDashboard.userTypes.veterinarian', 'Veterinarian'),
          SERVICE_PROVIDER: t('adminDashboard.userTypes.serviceProvider', 'Service Provider'),
          FIRM: t('adminDashboard.userTypes.firm', 'Firm'),
          ADMIN: t('adminDashboard.userTypes.admin', 'Admin'),
          CONTENT_ADMIN: t('adminDashboard.userTypes.contentAdmin', 'Content Admin'),
          DEV_ADMIN: t('adminDashboard.userTypes.devAdmin', 'Dev Admin'),
          CLINIC_MODERATOR: t('adminDashboard.userTypes.clinicModerator', 'Clinic Moderator'),
        };
        return labels[name] || name;
      }
      return 'User';
    }
    const rawType = type || 'User';

    const labels = {
      PET_GUARDIAN: t('adminDashboard.userTypes.petGuardian', 'Pet Guardian'),
      VETERINARIAN: t('adminDashboard.userTypes.veterinarian', 'Veterinarian'),
      SERVICE_PROVIDER: t('adminDashboard.userTypes.serviceProvider', 'Service Provider'),
      FIRM: t('adminDashboard.userTypes.firm', 'Firm'),
      ADMIN: t('adminDashboard.userTypes.admin', 'Admin'),
      CONTENT_ADMIN: t('adminDashboard.userTypes.contentAdmin', 'Content Admin'),
      DEV_ADMIN: t('adminDashboard.userTypes.devAdmin', 'Dev Admin'),
      CLINIC_MODERATOR: t('adminDashboard.userTypes.clinicModerator', 'Clinic Moderator'),
    };

    return labels[rawType] || rawType;
  };

  const filteredCountries = availableCountries.filter(c => {
    if (!countrySearch) return true;
    return c.name?.toLowerCase().includes(countrySearch.toLowerCase());
  });

  const getCountryLabel = (user) =>
    availableCountries.find(c => String(c.id) === String(user.country_id))?.name || '—';

  const formatJoinedDate = (raw) => {
    if (!raw) return '—';
    const d = new Date(raw);
    return isNaN(d) ? safeRender(raw) : d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setUserStats({ posts: 0, buddies: 0 });
    setDeleteError(null);
    setIsLoadingDetails(true);
    setIsLoadingStats(true);

    try {
      const response = await devAdminAPI.getUserDetails(user.id);
      const rawData = response.data;
      let details = rawData?.user || rawData?.data || rawData;
      if (Array.isArray(details) && details.length > 0) {
        details = details[0];
      }
      setSelectedUser(prev => ({
        ...prev,
        ...details,
        id: details?.id || details?.user_id || details?.pk || prev?.id,
        created_at: details?.created_at || details?.registration_date || prev?.created_at
      }));
    } catch (error) {
      console.error('Failed to load user details:', error);
    } finally {
      setIsLoadingDetails(false);
    }

    // Load stats in parallel
    try {
      const [postsRes, buddiesRes] = await Promise.allSettled([
        postsAPI.getOtherPosts(user.id, { per_page: 1 }),
        socialAPI.getConnects({ user_id: user.id, per_page: 1 })
      ]);

      let postsCount = 0;
      if (postsRes.status === 'fulfilled') {
        const data = postsRes.value?.data;
        postsCount = data?.meta?.total ?? data?.pagination?.total ?? data?.total ?? 0;
      }

      let buddiesCount = 0;
      if (buddiesRes.status === 'fulfilled') {
        const data = buddiesRes.value?.data;
        buddiesCount = data?.meta?.total ?? data?.pagination?.total ?? data?.total ?? 0;
      }

      setUserStats({ posts: postsCount, buddies: buddiesCount });
    } catch (e) {
      console.error('Failed to load user stats:', e);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleAction = async (actionKey, payload) => {
    if (!selectedUser || isActionProcessing) return;
    setIsActionProcessing(true);

    const targetName = getUserName(selectedUser);

    try {
      switch (actionKey) {
        case 'suspend':
          await devAdminAPI.suspendUser(selectedUser.id, { ...payload, targetName });
          break;
        case 'mute':
          await devAdminAPI.muteUser(selectedUser.id, { ...payload, targetName });
          break;
        case 'delete':
          await devAdminAPI.banUser(selectedUser.id, { ...payload, targetName });
          break;
        default:
          break;
      }
      setActionModal({ isOpen: false });
      setSelectedUser(null);
      loadUsers(currentPage);
    } catch (error) {
      console.error('Action failed:', error);
      setDeleteError(error?.response?.data?.message || 'Action failed. Please try again.');
    } finally {
      setIsActionProcessing(false);
    }
  };

  const userActions = [
    {
      key: 'suspend',
      label: t('moderation.actions.suspend', 'Suspend'),
      description: t('moderation.actions.suspendDesc', 'Temporarily disable account'),
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
        </svg>
      ),
      requiresDuration: true,
      variant: 'caution',
      confirmTitle: t('moderation.actions.suspendConfirmTitle', 'Suspend User'),
      confirmMessage: t('moderation.actions.suspendConfirmMessage', 'This user will be unable to log in until the suspension expires.'),
      confirmButton: t('moderation.actions.suspend', 'Suspend'),
    },
    {
      key: 'mute',
      label: t('moderation.actions.mute', 'Mute'),
      description: t('moderation.actions.muteDesc', 'Block posts, comments, and chat'),
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
          <line x1="23" y1="9" x2="17" y2="15"></line>
          <line x1="17" y1="9" x2="23" y2="15"></line>
        </svg>
      ),
      requiresDuration: true,
      variant: 'caution',
      confirmTitle: t('moderation.actions.muteConfirmTitle', 'Mute User'),
      confirmMessage: t('moderation.actions.muteConfirmMessage', 'This user will be able to browse but cannot create posts, comments, or send messages.'),
      confirmButton: t('moderation.actions.mute', 'Mute'),
    },
    {
      key: 'delete',
      label: t('moderation.actions.banUser', 'Ban User'),
      description: t('moderation.actions.banUserDesc', 'Block user from platform (data preserved)'),
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
        </svg>
      ),
      variant: 'danger',
      confirmTitle: t('moderation.actions.banConfirmTitle', 'Ban User'),
      confirmMessage: t('moderation.actions.banConfirmMessage', 'This user will be blocked from the platform. All their data will be preserved for potential appeal.'),
      confirmButton: t('moderation.actions.banUser', 'Ban'),
    },
  ];

  const safeRender = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (typeof value === 'object') {
      return value.name || value.display_name || value.label || JSON.stringify(value);
    }
    return String(value);
  };

  const petsCount = selectedUser?.pets?.length || 0;

  return (
    <div className="user-manager">
      <div className="user-manager-layout">
        {/* Left Panel - User Directory */}
        <div className="user-manager-panel user-manager-list user-manager-directory">
          <div className="user-manager-panel-header">
            <h3 className="user-manager-panel-title">{t('adminDashboard.userManagement.title', 'User Management')}</h3>
            <span className="user-manager-count">{pagination.total}</span>
          </div>
          <div className="user-manager-filters">
            <div className="user-manager-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder={t('adminDashboard.userManagement.searchPlaceholder', 'Search users by name or email...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="user-manager-search-clear"
                  onClick={() => setSearchQuery('')}
                  title={t('common.clear', 'Clear')}
                >
                  ✕
                </button>
              )}
            </div>
            {/* Country Filter */}
            <div className="user-manager-country-filter">
              <button
                className={`user-manager-country-filter-toggle ${countryFilter ? 'active' : ''}`}
                onClick={() => setCountryDropdownOpen(o => !o)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span className="user-manager-country-filter-label">
                  {countryFilter
                    ? availableCountries.find(c => String(c.id) === String(countryFilter))?.name || t('adminDashboard.userManagement.country', 'Country')
                    : t('adminDashboard.userManagement.allCountries', 'All Countries')}
                </span>
                <svg className={`user-manager-country-filter-chevron ${countryDropdownOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {countryDropdownOpen && (
                <div className="user-manager-country-filter-dropdown">
                  <div className="user-manager-country-filter-search">
                    <input
                      type="text"
                      placeholder={t('adminDashboard.userManagement.searchCountry', 'Search country...')}
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="user-manager-country-filter-list">
                    <button
                      className={`user-manager-country-filter-item ${!countryFilter ? 'active' : ''}`}
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      {t('adminDashboard.userManagement.allCountries', 'All Countries')}
                    </button>
                    {filteredCountries.map(country => (
                      <button
                        key={country.id}
                        className={`user-manager-country-filter-item ${String(countryFilter) === String(country.id) ? 'active' : ''}`}
                        onClick={() => { setCountryFilter(String(country.id)); setCountryDropdownOpen(false); setCountrySearch(''); }}
                      >
                        {country.name}
                      </button>
                    ))}
                  </div>
                  {countryFilter && (
                    <button
                      className="user-manager-country-filter-clear"
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      {t('adminDashboard.userManagement.clearFilter', 'Clear filter')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="user-manager-list-content user-manager-directory-content">
            {isLoadingUsers ? (
              <div className="user-manager-loading">{t('adminDashboard.userManagement.loading', 'Loading users...')}</div>
            ) : apiError ? (
              <div className="user-manager-empty user-manager-error">
                <p>{apiError}</p>
                <button className="user-manager-retry-btn" onClick={() => loadUsers(currentPage)}>
                  {t('common.retry', 'Retry')}
                </button>
              </div>
            ) : users.length === 0 ? (
              <div className="user-manager-empty">
                <p>{t('adminDashboard.userManagement.noUsers', 'No users found.')}</p>
              </div>
            ) : (
              <>
                <div className="user-manager-table-head">
                  <span>{t('adminDashboard.userManagement.userColumn', 'User')}</span>
                  <span>{t('adminDashboard.userManagement.userType', 'User Type')}</span>
                  <span>{t('adminDashboard.userManagement.country', 'Country')}</span>
                  <span>{t('adminDashboard.userManagement.registered', 'Registered')}</span>
                  <span></span>
                </div>
                {users.map(user => (
                  <div
                    key={user.id}
                    className={`user-manager-table-row ${selectedUser?.id === user.id ? 'selected' : ''}`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="user-manager-table-user">
                      {user.profile_image_url ? (
                        <img
                          src={user.profile_image_url}
                          alt={getUserName(user)}
                          className="user-manager-item-avatar"
                        />
                      ) : (
                        <div className="user-manager-item-avatar user-manager-item-avatar-placeholder">
                          {getUserName(user).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="user-manager-table-user-info">
                        <h4>{getUserName(user)}</h4>
                        <p>{typeof user.email === 'string' ? user.email : '—'}</p>
                      </div>
                    </div>
                    <span className="um-type-pill">{getUserType(user)}</span>
                    <span className="user-manager-table-cell">{getCountryLabel(user)}</span>
                    <span className="user-manager-table-cell user-manager-table-date">{formatJoinedDate(user.created_at || user.registration_date)}</span>
                    <button
                      className="user-manager-row-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectUser(user);
                        setActionModal({ isOpen: true });
                      }}
                    >
                      {t('adminDashboard.userManagement.actions', 'Actions')}
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="user-manager-pagination">
              <button
                disabled={currentPage <= 1}
                onClick={() => loadUsers(currentPage - 1)}
              >
                {t('common.prev', 'Prev')}
              </button>
              <span>{currentPage} / {pagination.last_page}</span>
              <button
                disabled={currentPage >= pagination.last_page}
                onClick={() => loadUsers(currentPage + 1)}
              >
                {t('common.next', 'Next')}
              </button>
            </div>
          )}
        </div>

        {/* Right Panel - User Details */}
        <div className="user-manager-panel user-manager-details">
          {!selectedUser ? (
            <div className="user-manager-details-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <p>{t('adminDashboard.userManagement.selectUser', 'Select a user to view details and manage their account.')}</p>
            </div>
          ) : (
            <>
              <div className="user-manager-details-header">
                <div className="user-manager-details-header-main">
                  {selectedUser.profile_image_url ? (
                    <img
                      src={selectedUser.profile_image_url}
                      alt={getUserName(selectedUser)}
                      className="user-manager-details-avatar"
                    />
                  ) : (
                    <div className="user-manager-details-avatar user-manager-details-avatar-placeholder">
                      {getUserName(selectedUser).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="user-manager-details-header-text">
                    <h2>{getUserName(selectedUser)}</h2>
                    <div className="user-manager-details-meta">
                      <span className="um-type-pill">{getUserType(selectedUser)}</span>
                      <span className="user-manager-details-meta-item">ID {selectedUser.id}</span>
                      <span className="user-manager-details-meta-item">
                        {selectedUser.country_data?.name_en
                          ? safeRender(selectedUser.country_data.name_en)
                          : selectedUser.country
                            ? safeRender(selectedUser.country)
                            : '—'}
                      </span>
                      <span className="user-manager-details-meta-item">{formatJoinedDate(selectedUser.created_at || selectedUser.registration_date)}</span>
                    </div>
                    <p className="user-manager-details-sub">
                      <span>{safeRender(selectedUser.email)}</span>
                      {selectedUser.username && <span>@{safeRender(selectedUser.username)}</span>}
                    </p>
                  </div>
                </div>
                <div className="user-manager-details-actions">
                  <button
                    className="user-manager-actions-btn"
                    onClick={() => setActionModal({ isOpen: true })}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="1"></circle>
                      <circle cx="19" cy="12" r="1"></circle>
                      <circle cx="5" cy="12" r="1"></circle>
                    </svg>
                    {t('adminDashboard.userManagement.actions', 'Actions')}
                  </button>
                </div>
              </div>

              <div className="user-manager-details-content">
                {isLoadingDetails ? (
                  <div className="user-manager-loading">{t('adminDashboard.userManagement.loadingDetails', 'Loading details...')}</div>
                ) : (
                  <>
                    {/* Fact strip */}
                    <div className="user-manager-fact-strip">
                      <div className="user-manager-fact">
                        <span className="user-manager-fact-label">{t('adminDashboard.userManagement.posts', 'Posts')}</span>
                        <span className="user-manager-fact-value">
                          {isLoadingStats ? '—' : userStats.posts}
                        </span>
                      </div>
                      <div className="user-manager-fact">
                        <span className="user-manager-fact-label">{t('adminDashboard.userManagement.buddies', 'Buddies')}</span>
                        <span className="user-manager-fact-value">
                          {isLoadingStats ? '—' : userStats.buddies}
                        </span>
                      </div>
                      <div className="user-manager-fact">
                        <span className="user-manager-fact-label">{t('adminDashboard.userManagement.pets', 'Pets')}</span>
                        <span className="user-manager-fact-value">{petsCount}</span>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="user-manager-section">
                      <h3>{t('adminDashboard.userManagement.userInfo', 'User Information')}</h3>
                      <div className="user-manager-info-grid">
                        <div className="user-manager-info-item">
                          <label>{t('adminDashboard.userManagement.email', 'Email')}</label>
                          <span>{safeRender(selectedUser.email)}</span>
                        </div>
                        <div className="user-manager-info-item">
                          <label>{t('adminDashboard.userManagement.phone', 'Phone')}</label>
                          <span>{safeRender(selectedUser.phone || selectedUser.phone_number)}</span>
                        </div>
                        <div className="user-manager-info-item">
                          <label>{t('adminDashboard.userManagement.location', 'Country / City')}</label>
                          <span>{(() => {
                            if (selectedUser.full_address) return selectedUser.full_address;
                            const country = selectedUser.country_data?.name_en || selectedUser.country;
                            const city = selectedUser.city_data?.name_en || selectedUser.city;
                            if (country && city) return `${country}, ${city}`;
                            if (country) return country;
                            if (city) return city;
                            return '—';
                          })()}</span>
                        </div>
                        <div className="user-manager-info-item">
                          <label>{t('adminDashboard.userManagement.registered', 'Registered')}</label>
                          <span>{(() => {
                            const raw = selectedUser.created_at || selectedUser.registration_date;
                            if (!raw) return '—';
                            const d = new Date(raw);
                            return isNaN(d) ? safeRender(raw) : d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
                          })()}</span>
                        </div>
                        <div className="user-manager-info-item">
                          <label>{t('adminDashboard.userManagement.userType', 'User Type')}</label>
                          <span>{getUserType(selectedUser)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Latest Moderation Action */}
                    {selectedUser.latest_action && (
                      <div className="user-manager-section">
                        <h3>{t('adminDashboard.userManagement.latestAction', 'Latest Action')}</h3>
                        <div className="user-manager-action-card">
                          <div className="user-manager-action-header">
                            <span className={`user-manager-action-badge ${selectedUser.latest_action.action}`}>
                              {selectedUser.latest_action.action}
                            </span>
                            {selectedUser.latest_action.duration_hours && (
                              <span className="user-manager-action-duration">
                                {selectedUser.latest_action.duration_hours}h
                              </span>
                            )}
                          </div>
                          {selectedUser.latest_action.reasons?.length > 0 && (
                            <div className="user-manager-action-reasons">
                              {selectedUser.latest_action.reasons.map(r => (
                                <span key={r} className="user-manager-action-reason-tag">
                                  {t(`reportReasons.${r}`, r)}
                                </span>
                              ))}
                            </div>
                          )}
                          {selectedUser.latest_action.reason_text && (
                            <p className="user-manager-action-text">{selectedUser.latest_action.reason_text}</p>
                          )}
                          {selectedUser.latest_action.images?.length > 0 && (
                            <div className="user-manager-action-images">
                              {selectedUser.latest_action.images.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                  <img src={url} alt="" />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pets List */}
                    {petsCount > 0 && (
                      <div className="user-manager-section">
                        <h3>{t('adminDashboard.userManagement.ownedPets', 'Owned Pets')}</h3>
                        <div className="user-manager-pet-list">
                          {selectedUser.pets.map(pet => (
                            <div key={pet.id} className="user-manager-pet-row">
                              <div className="user-manager-pet-thumb">
                                {pet.profile_image_url ? (
                                  <img src={pet.profile_image_url} alt={typeof pet.name === 'string' ? pet.name : 'Pet'} />
                                ) : (
                                  <img src="/paw-svgrepo-com.svg" alt="" className="user-manager-pet-thumb-fallback" width="20" height="20" />
                                )}
                              </div>
                              <div className="user-manager-pet-info">
                                <h4>{pet.name ? safeRender(pet.name) : 'Unknown'}</h4>
                                <p>{safeRender(pet.species || '—')} {pet.breed ? `• ${safeRender(pet.breed)}` : ''}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Moderation Actions Modal */}
      <ModerationActionModal
        isOpen={actionModal.isOpen}
        onClose={() => setActionModal({ isOpen: false })}
        title={t('moderation.actions.title', 'Take Action')}
        actions={userActions}
        onAction={handleAction}
        isProcessing={isActionProcessing}
      />

      {/* Error Toast */}
      {deleteError && (
        <div className="user-manager-error-toast">
          <div className="user-manager-error-content">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span>{deleteError}</span>
            <button onClick={() => setDeleteError(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
